/**
 * NotificationBell — in-app notification bell + center (gap 5).
 *
 * A bell icon (AntD Badge + BellOutlined) shown to any authenticated user in
 * the nav bar. It polls the unread count every ~45s and refetches the list
 * whenever the popover opens. Clicking a notification navigates to wherever
 * its `link_url` points, marks it read, and refreshes; a "Mark all read"
 * action clears everything. Self-contained — owns its own data fetching via
 * appSupportService and reads auth from AppContext.
 *
 * ONE BELL, ONE TABLE.
 * This reads the SHARED `app_support` queue, not this app's old local one.
 * Both existed for a while and that was the bug: `app_support` wrote feedback
 * status changes to `app_notifications` while this component read
 * `broker_deal_notifications`, so from the day the feedback page was
 * re-pointed (#177) every "your report is ready to re-test" went somewhere
 * nothing rendered and reporters were told nothing at all. Deal assignment
 * and approvals now ride the same queue, tagged `app=broker_deals`. See
 * wig-broker-deals#190 and de-hub's `_flush_notifications`.
 *
 * So: do not reintroduce a second source here. If something needs to reach
 * this bell, it emits into `app_support`.
 *
 * Server scopes every read to the caller (recipient_user = me OR
 * recipient_role ∈ my roles), so there is no client-side filtering here.
 *
 * The popover has two views, toggled by the gear in its header:
 *   'list'     — the notifications themselves (the default)
 *   'settings' — one switch per event type, so a user can mute the kinds of
 *                notification they don't care about.
 *
 * Preferences are SERVER-side (see the /notifications/prefs endpoints), and
 * the server applies them to both the list and the unread count. That matters
 * here for two reasons: the badge can never disagree with the list, and this
 * component must not filter anything locally — a muted row simply never
 * arrives. The mute vocabulary is likewise the server's: `prefs` renders
 * whatever keys the API returns rather than a hard-coded list, so a new event
 * type gets a switch without a frontend change.
 *
 * EMAIL switches come from a separate endpoint (/notifications/email_prefs)
 * that only lists the events the user may toggle for email. When it returns
 * any, the settings view grows an "In-app" / "Email" column pair and an email
 * switch appears only on those events. If that GET fails (e.g. 404 before the
 * backend is deployed) the email column is hidden and the view behaves exactly
 * as it did before. Both maps load together so nothing shifts when they land.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router";
import {
  Badge,
  Button,
  Empty,
  List,
  Popover,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  BellOutlined,
  CheckOutlined,
  SettingOutlined,
} from "@ant-design/icons";

import { AppContext } from "../AppContextProvider";
import appSupportService from "../services/appSupportService";

const { Text } = Typography;

// How often (ms) to refresh the unread badge while mounted.
const POLL_MS = 45000;
// How many recent rows to pull into the center when opened.
const LIST_LIMIT = 20;
// Width (px) of each switch column in the settings view when the Email
// column is shown, so the "In-app" / "Email" headers line up with the rows.
const PREF_COL_WIDTH = 48;

// Lightweight relative-time formatter (no dayjs dependency in this app).
// Falls back to a locale date string for anything older than ~30 days.
const relativeTime = (value) => {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return String(value);
  const secs = Math.round((Date.now() - then.getTime()) / 1000);
  if (secs < 0) return "just now";
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString();
};

const EVENT_LABEL = {
  approval_requested: { text: "Approval requested", color: "blue" },
  approval_decided: { text: "Approval decided", color: "green" },
  deal_assigned: { text: "Deal assigned", color: "purple" },
  feedback_submitted: { text: "New feedback", color: "orange" },
  feedback_replied: { text: "Feedback reply", color: "cyan" },
  feedback_status_changed: { text: "Feedback update", color: "cyan" },
};

// What each switch in the settings view promises to suppress. Only the "who
// else gets this" half is worth spelling out — that is what makes the bell
// feel noisy, since two of the three event types are broadcast to a whole
// role rather than to one person.
const EVENT_HINT = {
  approval_requested: "Sent to everyone who can approve capex.",
  approval_decided: "Sent to you when a request you submitted is decided.",
  deal_assigned: "Sent to every broker agent when a deal changes hands.",
  feedback_submitted:
    "Sent to every app admin when someone files a bug or feature request.",
  feedback_replied: "Sent when someone replies on a request you're part of.",
  feedback_status_changed:
    "Sent to you when a request you filed changes status.",
};

// Fallback label for an event type the frontend has not been taught yet — the
// server owns the vocabulary, so this keeps a newly added type readable
// instead of dropping it from the settings list.
const humanize = (eventType) =>
  String(eventType)
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

const NotificationBell = () => {
  const navigate = useNavigate();
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);

  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // 'list' | 'settings' — which face of the popover is showing.
  const [view, setView] = useState("list");
  // Map of event_type -> enabled, straight from the server (null until the
  // settings view has been opened at least once).
  const [prefs, setPrefs] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(false);
  // The event type currently being written, so only its own switch spins.
  const [savingKey, setSavingKey] = useState(null);
  // Map of event_type -> email enabled, holding ONLY the events the user may
  // toggle for email. null = unavailable (not loaded, or the endpoint failed),
  // in which case no email switches render and the view is in-app only.
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [emailSavingKey, setEmailSavingKey] = useState(null);

  // Track mount so async resolutions after unmount don't setState.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refreshCount = useCallback(async () => {
    try {
      const { token: tk, userOid } = await getAuth();
      if (!tk) return;
      const resp = await appSupportService.getUnreadCount({
        token: tk,
        userOid,
      });
      const count = resp?.data?.data?.unread_count ?? 0;
      if (mountedRef.current) setUnreadCount(count);
    } catch (err) {
      // Non-fatal: badge just won't update this cycle.
      console.error("Failed to load notification count:", err?.message || err);
    }
  }, [getAuth]);

  const refreshList = useCallback(async () => {
    try {
      setLoading(true);
      const { token: tk, userOid } = await getAuth();
      if (!tk) return;
      const resp = await appSupportService.listNotifications({
        token: tk,
        userOid,
        limit: LIST_LIMIT,
      });
      const rows = resp?.data?.data || [];
      if (mountedRef.current) setItems(rows);
    } catch (err) {
      console.error("Failed to load notifications:", err?.message || err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [getAuth]);

  // Email prefs are optional: any failure (e.g. a 404 before the endpoint is
  // deployed) resolves to null so the view silently stays in-app only.
  const fetchEmailPrefs = useCallback(async (tk, userOid) => {
    try {
      const resp = await appSupportService.getNotificationEmailPrefs({
        token: tk,
        userOid,
      });
      const map = resp?.data?.data;
      return map && typeof map === "object" ? map : null;
    } catch {
      return null;
    }
  }, []);

  const refreshPrefs = useCallback(async () => {
    try {
      setPrefsLoading(true);
      const { token: tk, userOid } = await getAuth();
      if (!tk) return;
      // Both maps are fetched together and applied together, so the email
      // column never pops in after the in-app switches have drawn.
      const [resp, emailMap] = await Promise.all([
        appSupportService.getNotificationPrefs({ token: tk, userOid }),
        fetchEmailPrefs(tk, userOid),
      ]);
      const map = resp?.data?.data;
      if (mountedRef.current) {
        setEmailPrefs(emailMap);
        if (map) setPrefs(map);
      }
    } catch (err) {
      console.error(
        "Failed to load notification preferences:",
        err?.message || err
      );
      if (mountedRef.current) {
        message.error("Couldn't load your notification settings.");
      }
    } finally {
      if (mountedRef.current) setPrefsLoading(false);
    }
  }, [getAuth, fetchEmailPrefs]);

  // Poll the unread count on mount and every POLL_MS; clean up on unmount.
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  // Refetch the list whenever the center is opened, and always reopen on the
  // list — the settings view is a detour, not a place to leave someone.
  const handleOpenChange = useCallback(
    (next) => {
      setOpen(next);
      if (next) {
        setView("list");
        refreshList();
      }
    },
    [refreshList]
  );

  const handleItemClick = useCallback(
    async (n) => {
      setOpen(false);
      // `link_url` is an app-RELATIVE path composed server-side: /feedback?
      // request=<id> for the feedback trio, /deals/<key> for the deal ones.
      // It is now the ONLY source — the shared `app_notifications` table has
      // no `deal_key` column, because a cross-app bell cannot carry one app's
      // foreign keys, so the old fallback that read it is gone. Rows migrated
      // from the local table had their deal_key folded into link_url on the
      // way across, so nothing lost its destination.
      //
      // The leading-slash check is belt and braces: the server only ever
      // writes a relative path, but this value goes straight into navigate(),
      // and a row that somehow held an absolute URL would be an open
      // redirect. A protocol-relative "//evil.example" is rejected too.
      if (typeof n?.link_url === "string" && /^\/(?!\/)/.test(n.link_url)) {
        navigate(n.link_url);
      }
      if (!n?.is_read) {
        try {
          const { token: tk, userOid } = await getAuth();
          await appSupportService.markNotificationRead({
            token: tk,
            userOid,
            id: n.id,
          });
        } catch (err) {
          console.error("Failed to mark notification read:", err?.message || err);
        }
        refreshCount();
        refreshList();
      }
    },
    [navigate, getAuth, refreshCount, refreshList]
  );

  const handleMarkAll = useCallback(async () => {
    try {
      const { token: tk, userOid } = await getAuth();
      await appSupportService.markAllNotificationsRead({ token: tk, userOid });
    } catch (err) {
      console.error("Failed to mark all read:", err?.message || err);
    }
    refreshCount();
    refreshList();
  }, [getAuth, refreshCount, refreshList]);

  const handleOpenSettings = useCallback(() => {
    setView("settings");
    refreshPrefs();
  }, [refreshPrefs]);

  // Back to the list — refetch it, since a mute flipped while in settings
  // changes which rows the server will hand back.
  const handleCloseSettings = useCallback(() => {
    setView("list");
    refreshList();
  }, [refreshList]);

  const handleTogglePref = useCallback(
    async (eventType, enabled) => {
      const prior = prefs;
      // Optimistic: the switch should move under the finger, not after a
      // round-trip. The server response replaces this either way.
      setPrefs((p) => ({ ...(p || {}), [eventType]: enabled }));
      setSavingKey(eventType);
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await appSupportService.updateNotificationPrefs({
          token: tk,
          userOid,
          prefs: { [eventType]: enabled },
        });
        const map = resp?.data?.data;
        if (mountedRef.current && map) setPrefs(map);
        // A mute changes the badge as well as the list, so re-poll rather
        // than waiting up to POLL_MS for the count to catch up.
        refreshCount();
      } catch (err) {
        console.error(
          "Failed to save notification preference:",
          err?.message || err
        );
        if (mountedRef.current) {
          setPrefs(prior);
          message.error("Couldn't save that notification setting.");
        }
      } finally {
        if (mountedRef.current) setSavingKey(null);
      }
    },
    [prefs, getAuth, refreshCount]
  );

  // Same optimistic-then-revert pattern as handleTogglePref. Email does not
  // affect the bell's list or badge, so there is no count re-poll here.
  const handleToggleEmailPref = useCallback(
    async (eventType, enabled) => {
      const prior = emailPrefs;
      setEmailPrefs((p) => ({ ...(p || {}), [eventType]: enabled }));
      setEmailSavingKey(eventType);
      try {
        const { token: tk, userOid } = await getAuth();
        const resp = await appSupportService.updateNotificationEmailPrefs({
          token: tk,
          userOid,
          prefs: { [eventType]: enabled },
        });
        const map = resp?.data?.data;
        if (mountedRef.current && map) setEmailPrefs(map);
      } catch (err) {
        console.error(
          "Failed to save email notification preference:",
          err?.message || err
        );
        if (mountedRef.current) {
          setEmailPrefs(prior);
          message.error("Couldn't save that email setting.");
        }
      } finally {
        if (mountedRef.current) setEmailSavingKey(null);
      }
    },
    [emailPrefs, getAuth]
  );

  const listView = (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Text strong>Notifications</Text>
        <div>
          <Tooltip title="Notification settings">
            <Button
              type="link"
              size="small"
              aria-label="Notification settings"
              icon={<SettingOutlined />}
              onClick={handleOpenSettings}
            />
          </Tooltip>
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            disabled={unreadCount === 0}
            onClick={handleMarkAll}
          >
            Mark all read
          </Button>
        </div>
      </div>
      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notifications"
        />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(n) => {
            const meta = EVENT_LABEL[n.event_type];
            return (
              <List.Item
                onClick={() => handleItemClick(n)}
                style={{
                  cursor: "pointer",
                  alignItems: "flex-start",
                  background: n.is_read ? undefined : "rgba(22,104,220,0.08)",
                  paddingInline: 8,
                  borderRadius: 4,
                }}
              >
                <List.Item.Meta
                  title={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <Text strong={!n.is_read} ellipsis style={{ flex: 1 }}>
                        {n.title}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                        {relativeTime(n.created_at)}
                      </Text>
                    </div>
                  }
                  description={
                    <div>
                      {n.body ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {n.body}
                        </Text>
                      ) : null}
                      {meta ? (
                        <div style={{ marginTop: 4 }}>
                          <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
                            {meta.text}
                          </Tag>
                        </div>
                      ) : null}
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </>
  );

  const prefEntries = Object.entries(prefs || {});
  // Two columns only when the server offered at least one email switch;
  // otherwise the view is exactly the original single-switch layout.
  const showEmail = !!emailPrefs && Object.keys(emailPrefs).length > 0;

  const renderPrefControls = (eventType, enabled) => {
    const label = EVENT_LABEL[eventType]?.text || humanize(eventType);
    const inAppSwitch = (
      <Switch
        size="small"
        checked={enabled}
        loading={savingKey === eventType}
        aria-label={label}
        onChange={(next) => handleTogglePref(eventType, next)}
      />
    );
    if (!showEmail) return inAppSwitch;
    const hasEmail = Object.prototype.hasOwnProperty.call(
      emailPrefs,
      eventType
    );
    return (
      <div style={{ display: "flex" }}>
        <div style={{ width: PREF_COL_WIDTH, textAlign: "center" }}>
          {inAppSwitch}
        </div>
        <div style={{ width: PREF_COL_WIDTH, textAlign: "center" }}>
          {hasEmail ? (
            <Switch
              size="small"
              checked={!!emailPrefs[eventType]}
              loading={emailSavingKey === eventType}
              aria-label={`Email: ${label}`}
              onChange={(next) => handleToggleEmailPref(eventType, next)}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const settingsView = (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          marginBottom: 8,
        }}
      >
        <Button
          type="link"
          size="small"
          aria-label="Back to notifications"
          icon={<ArrowLeftOutlined />}
          onClick={handleCloseSettings}
        />
        <Text strong>Notification settings</Text>
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Choose what reaches your bell. Turning one off hides it from both the
        list and the badge — turn it back on and you get the ones you missed.
      </Text>
      {prefsLoading && prefEntries.length === 0 ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : prefEntries.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notification types"
        />
      ) : (
        <>
        {showEmail ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 8,
              paddingInline: 8,
            }}
          >
            {["In-app", "Email"].map((h) => (
              <Text
                key={h}
                type="secondary"
                style={{ width: PREF_COL_WIDTH, textAlign: "center", fontSize: 11 }}
              >
                {h}
              </Text>
            ))}
          </div>
        ) : null}
        <List
          size="small"
          style={{ marginTop: showEmail ? 0 : 8 }}
          dataSource={prefEntries}
          renderItem={([eventType, enabled]) => (
            <List.Item
              style={{ alignItems: "flex-start", paddingInline: 8 }}
              extra={renderPrefControls(eventType, enabled)}
            >
              <List.Item.Meta
                title={
                  <Text>
                    {EVENT_LABEL[eventType]?.text || humanize(eventType)}
                  </Text>
                }
                description={
                  EVENT_HINT[eventType] ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {EVENT_HINT[eventType]}
                    </Text>
                  ) : null
                }
              />
            </List.Item>
          )}
        />
        </>
      )}
    </>
  );

  const content = (
    <div style={{ width: 340, maxHeight: 420, overflowY: "auto" }}>
      {view === "settings" ? settingsView : listView}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Badge count={unreadCount} size="small" overflowCount={99}>
        <Button
          type="text"
          shape="circle"
          aria-label="Notifications"
          icon={<BellOutlined style={{ fontSize: 18, color: "#ffffff" }} />}
        />
      </Badge>
    </Popover>
  );
};

export default NotificationBell;
